import { createRoot } from 'react-dom/client';
import './styles/tokens.css';
import './styles/base.css';
import { App } from './app/App';
import { bootSession, hydrate, isDemo, loadDemo } from './data/session';
import { startPerfMarks } from './lib/perf';
import { checkWebVersion, setServiceWorker } from './lib/refresh';
import { initRouter } from './lib/router';
import { warmUp } from './lib/warmup';

// A page left open across an update asks for code files the server has since replaced. When
// that happens, reload onto the current version instead of breaking (once per minute at most).
window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault();
  try {
    const last = Number(sessionStorage.getItem('trips.chunkReload') ?? 0);
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem('trips.chunkReload', String(Date.now()));
  } catch {
    // No storage: reload anyway.
  }
  location.reload();
});

initRouter();
if (isDemo()) loadDemo();
else {
  try {
    await hydrate();
  } catch (err) {
    console.error('Could not read local data', err);
  }
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
startPerfMarks();
warmUp();
void bootSession();

if (
  !__NATIVE__ &&
  !__PREVIEW__ &&
  'serviceWorker' in navigator &&
  import.meta.env.PROD &&
  !isDemo()
) {
  // The installed web app (and any open tab) checks for a new version when it comes back to
  // the front and every 15 minutes; a new one takes over and the page reloads onto it.
  void import('virtual:pwa-register').then(({ registerSW }) =>
    registerSW({
      immediate: true,
      onRegisteredSW(_url, registration) {
        if (!registration) return;
        setServiceWorker(registration);
        // A backstop for a page stuck on an old service worker: ask which build is current.
        const check = () => void checkWebVersion();
        setInterval(check, 15 * 60_000);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') check();
        });
        window.addEventListener('online', check);
      },
    }),
  );
}
